import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { submitChangeRequest } from '../../api/journeys'

function FormField({ label, required, children, error }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <span className="text-xs text-red-500">{error}</span>
      )}
    </div>
  )
}

function RequestChangeModal({ open, onClose, tenantId, template }) {
  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    description: '',
    urgency: 'normal',
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) {
      setForm({ contact_name: '', contact_email: '', description: '', urgency: 'normal' })
      setErrors({})
      setSubmitting(false)
      setSubmitted(false)
    }
  }, [open])

  function validate() {
    const errs = {}
    if (!form.contact_name.trim()) {
      errs.contact_name = 'Name is required'
    }
    if (form.description.trim().length < 20) {
      errs.description = 'Please describe your request in at least 20 characters'
    }
    return errs
  }

  async function handleSubmit() {
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      await submitChangeRequest(tenantId, {
        template_id: template.id,
        template_name: template.name,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim() || undefined,
        description: form.description.trim(),
        urgency: form.urgency,
      })
      setSubmitted(true)
      setTimeout(() => {
        onClose()
      }, 3000)
    } catch (err) {
      setErrors({ submit: 'Failed to submit request. Please try again.' })
      Sentry.captureException(err, {
        tags: { action: 'submit_change_request' },
        extra: { templateId: template?.id },
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const inputClass = `border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1535c8]/30 focus:border-[#1535c8] transition-colors w-full`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-slate-800 text-base">Request a Change</h2>
            <p className="text-xs text-slate-400 mt-0.5">{template?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">✅</div>
              <div className="font-semibold text-slate-800 mb-1">Request submitted!</div>
              <div className="text-sm text-slate-500">
                Our team will contact you within 24 hours. This dialog will close automatically.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">

              <FormField label="Your name" required error={errors.contact_name}>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="Enter your name"
                  value={form.contact_name}
                  onChange={e => setForm({ ...form, contact_name: e.target.value })}
                />
              </FormField>

              <FormField label="Your email (optional)">
                <input
                  type="email"
                  className={inputClass}
                  placeholder="your@email.com"
                  value={form.contact_email}
                  onChange={e => setForm({ ...form, contact_email: e.target.value })}
                />
              </FormField>

              <FormField label="Urgency">
                <div className="flex gap-3">
                  {['normal', 'urgent'].map(u => (
                    <label
                      key={u}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                        form.urgency === u
                          ? 'border-[#1535c8] bg-[#e8ecff] text-[#1535c8]'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="urgency"
                        value={u}
                        className="hidden"
                        checked={form.urgency === u}
                        onChange={() => setForm({ ...form, urgency: u })}
                      />
                      {u === 'normal' ? '📋 Normal' : '🚨 Urgent'}
                    </label>
                  ))}
                </div>
              </FormField>

              <FormField
                label="What would you like to change?"
                required
                error={errors.description}
              >
                <textarea
                  className={inputClass}
                  rows={4}
                  placeholder="Describe the changes you need in detail (minimum 20 characters)..."
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
                <span className="text-xs text-slate-400 text-right">
                  {form.description.length} chars
                  {form.description.length < 20 &&
                    ` (${20 - form.description.length} more needed)`}
                </span>
              </FormField>

              {errors.submit && (
                <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">
                  {errors.submit}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-[#1535c8] text-white rounded-lg hover:bg-[#1020a0] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default RequestChangeModal
